// Invisible Turnstile → session-token flow for the translate worker.
//
// Turnstile tokens are single-use, so instead of running a challenge per
// translate call the app runs Turnstile once, exchanges the token at the
// worker's POST /session endpoint for a short-lived signed session token, and
// sends that as X-T2E-Session on every translate call until it expires.

import { deployTokenHeader } from './deploy-token'

// Sitekeys are public by design. The dev key is Cloudflare's official
// invisible always-passes test key, matching the test secret in the worker's
// .dev.vars. TODO: replace the production value with the real sitekey from
// the Turnstile dashboard before flipping SESSION_ENFORCEMENT to 'on'.
const TURNSTILE_SITE_KEY = import.meta.env.DEV
   ? '1x00000000000000000000BB'
   : '0x4AAAAAAD2czx04Pwrup9No'

type Turnstile = {
   render: (container: HTMLElement, params: Record<string, unknown>) => string | undefined
   execute: (widgetId: string) => void
   remove: (widgetId: string) => void
}

declare global {
   interface Window {
      turnstile?: Turnstile
   }
}

let scriptPromise: Promise<void> | null = null

function loadTurnstileScript(): Promise<void> {
   if (window.turnstile) {
      return Promise.resolve()
   }
   if (!scriptPromise) {
      scriptPromise = new Promise((resolve, reject) => {
         const script = document.createElement('script')
         script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
         script.async = true
         script.defer = true
         script.onload = () => resolve()
         script.onerror = () => {
            scriptPromise = null
            reject(new Error('Failed to load Turnstile script'))
         }
         document.head.appendChild(script)
      })
   }
   return scriptPromise
}

// A challenge that never calls back leaves `inFlight` pinned forever, so the
// background priming below would never retry for the rest of the page's life.
// Bounded so a broken challenge degrades to "no session token" instead.
const TURNSTILE_TIMEOUT_MS = 10_000

async function runTurnstile(): Promise<string> {
   await loadTurnstileScript()

   return new Promise((resolve, reject) => {
      const container = document.createElement('div')
      // Invisible widgets render no UI; keep the container out of the layout.
      container.style.position = 'fixed'
      container.style.bottom = '0'
      container.style.right = '0'
      document.body.appendChild(container)

      let widgetId: string | undefined
      let timeoutId: ReturnType<typeof setTimeout> | undefined
      const cleanup = () => {
         if (timeoutId !== undefined) clearTimeout(timeoutId)
         try {
            if (widgetId) window.turnstile?.remove(widgetId)
         } catch {
            // Widget may already be gone.
         }
         container.remove()
      }

      timeoutId = setTimeout(() => {
         cleanup()
         reject(new Error('Turnstile challenge timed out'))
      }, TURNSTILE_TIMEOUT_MS)

      try {
         widgetId = window.turnstile?.render(container, {
            sitekey: TURNSTILE_SITE_KEY,
            execution: 'execute',
            callback: (token: string) => {
               cleanup()
               resolve(token)
            },
            'error-callback': () => {
               cleanup()
               reject(new Error('Turnstile challenge failed'))
            },
         })
         if (!widgetId) {
            throw new Error('Turnstile render failed')
         }
         window.turnstile?.execute(widgetId)
      } catch (err) {
         cleanup()
         reject(err)
      }
   })
}

type Session = { token: string; expiresAt: number }

let cached: Session | null = null
let inFlight: Promise<string> | null = null

// Entry pages are SSR'd full page loads, so a visitor who reads an entry and
// then translates used to re-run Turnstile and POST /session every time —
// module scope does not survive a document load. sessionStorage is the right
// scope for it: tab-local, gone when the tab closes, and never shared with
// another visitor the way a server-side cache would be.
const SESSION_STORAGE_KEY = 't2e-session'

function isFresh(session: Session): boolean {
   return session.expiresAt - 60_000 > Date.now()
}

function readStoredSession(): Session | null {
   try {
      const raw = sessionStorage.getItem(SESSION_STORAGE_KEY)
      if (!raw) return null
      const parsed: unknown = JSON.parse(raw)
      if (
         typeof parsed !== 'object' ||
         parsed === null ||
         typeof (parsed as Session).token !== 'string' ||
         typeof (parsed as Session).expiresAt !== 'number'
      ) {
         return null
      }
      const session = parsed as Session
      return isFresh(session) ? session : null
   } catch {
      // No sessionStorage (SSR), storage disabled, or unparseable contents:
      // fall through and acquire a token the usual way.
      return null
   }
}

function storeSession(session: Session): void {
   try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session))
   } catch {
      // Persisting is an optimisation; the in-memory token still works.
   }
}

/**
 * Returns a valid session token for the translate worker, running Turnstile
 * and calling POST /session only when the cached token is missing or close to
 * expiry. Concurrent callers share one in-flight acquisition.
 */
export function getSessionToken(workerUrl: string): Promise<string> {
   if (cached && isFresh(cached)) {
      return Promise.resolve(cached.token)
   }

   // Nothing in memory: this may be the first translate of a fresh page load,
   // so look for a token an earlier load in this tab already paid for.
   const stored = readStoredSession()
   if (stored) {
      cached = stored
      return Promise.resolve(stored.token)
   }

   if (!inFlight) {
      inFlight = (async () => {
         try {
            const turnstileToken = await runTurnstile()
            const response = await fetch(`${workerUrl}/session`, {
               method: 'POST',
               headers: { 'Content-Type': 'application/json', ...deployTokenHeader },
               body: JSON.stringify({ token: turnstileToken }),
            })
            if (!response.ok) {
               throw new Error(`Session endpoint returned ${response.status}`)
            }
            const data = (await response.json()) as {
               sessionToken: string
               expiresAt: number
            }
            cached = { token: data.sessionToken, expiresAt: data.expiresAt }
            storeSession(cached)
            return data.sessionToken
         } finally {
            inFlight = null
         }
      })()
   }
   return inFlight
}

/**
 * The token if one is already in hand, else `undefined` — never waits. When
 * nothing is cached it starts an acquisition in the background so a later call
 * can use it, but the caller is not held up.
 *
 * Translate calls use this rather than awaiting `getSessionToken`: the header
 * is advisory while the worker runs SESSION_ENFORCEMENT in 'log' mode, so
 * blocking on the handshake bought nothing and cost a full challenge round trip
 * on every page load. If enforcement is ever flipped to 'on', the worker
 * answers 401 and the caller's existing retry path awaits a real token before
 * trying again.
 */
export function getSessionTokenIfReady(workerUrl: string): string | undefined {
   if (cached && isFresh(cached)) {
      return cached.token
   }

   const stored = readStoredSession()
   if (stored) {
      cached = stored
      return stored.token
   }

   // Warm the cache for subsequent translate calls in this tab. Failures are
   // expected (a blocked or failing challenge) and are not this call's problem.
   void getSessionToken(workerUrl).catch(() => {})
   return undefined
}

/** Drops the cached token so the next call re-runs Turnstile (e.g. after a 401). */
export function clearSessionToken(): void {
   cached = null
   try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY)
   } catch {
      // Nothing persisted, or storage is unavailable.
   }
}
